import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { MarketErrorBanner } from '@/components/MarketErrorBanner';
import { Spinner } from '@/components/ui';
import Explore from '@/pages/Explore';

/**
 * Explore is the landing route and stays in the entry chunk. The rest load on
 * demand — the launch wizard and the token terminal are both heavy, and neither
 * is on the first-paint path.
 */
const Launch = lazy(() => import('@/pages/Launch'));
const Token = lazy(() => import('@/pages/Token'));
const Portfolio = lazy(() => import('@/pages/Portfolio'));
const NotFound = lazy(() => import('@/pages/NotFound'));

function RouteFallback() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center" role="status" aria-label="Loading">
      <Spinner size={22} className="text-bull" />
    </div>
  );
}

export default function App() {
  // Keying the boundary on the path means a crashed screen recovers as soon as the
  // user navigates away, rather than staying broken until a full reload.
  const { pathname } = useLocation();

  return (
    <AppShell>
      <MarketErrorBanner />
      <ErrorBoundary resetKey={pathname}>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Explore />} />
            <Route path="/launch" element={<Launch />} />
            <Route path="/token/:address" element={<Token />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/profile" element={<Navigate to="/portfolio" replace />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </AppShell>
  );
}
